import React, { FC, ReactNode } from 'react';
import { styled } from '@beeline.com/bp-design-system/src/styles';
import { Typography } from '@beeline.com/bp-design-system/src/components/Typography/Typography';
import completedIcon from './images/completed-task.svg';
import taskIssuesIcon from './images/task-has-issue.svg';

interface Props {
  taskId?: string;
  issueType: TaskSummaryType;
  children?: ReactNode;
  messages: {
    thankYou: string;
    accessDenied: string;
    checkSpam: string;
    expired: string;
    taskApproved: string;
    taskRejected: string;
    notAvailable: string;
    warning: string;
    activationExceeded: string;
    generationExceeded: string;
  };
}

export type TaskSummaryType =
  | 'approved'
  | 'rejected'
  | 'expired'
  | 'activationExceeded'
  | 'generationExceeded'
  | 'denied';

export const ExternalTaskSummary: FC<React.PropsWithChildren<Props>> = ({ issueType, children, messages }) => {
  const {
    thankYou,
    accessDenied,
    checkSpam,
    expired,
    taskApproved,
    taskRejected,
    notAvailable,
    warning,
    activationExceeded,
    generationExceeded
  } = messages;
  let title = '';
  let body1 = '';
  let instructions = '';

  if (issueType === 'approved' || issueType === 'rejected') title = thankYou;
  else if (issueType === 'denied') title = accessDenied;
  else title = expired;

  if (issueType === 'approved') body1 = taskApproved;
  else if (issueType === 'rejected') body1 = taskRejected;
  else if (issueType === 'denied') body1 = notAvailable;
  else if (issueType === 'activationExceeded') body1 = activationExceeded;
  else if (issueType === 'generationExceeded') body1 = generationExceeded;
  else body1 = warning;

  if (issueType === 'expired' || issueType === 'activationExceeded' || issueType === 'generationExceeded')
    instructions = checkSpam;

  return (
    <StyledContainer>
      <div>
        <img
          src={issueType === 'approved' || issueType === 'rejected' ? completedIcon : taskIssuesIcon}
          alt="completed icon"
        />
        <Typography dataTest="summary-title" variant="h4">
          {title}
        </Typography>
        <Typography dataTest="summary-body" variant="body2">
          {body1}
        </Typography>
        {instructions && <Typography variant="body1Bold">{instructions}</Typography>}
        {children}
      </div>
    </StyledContainer>
  );
};

const StyledContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: calc(100% - 2rem);

  > div {
    width: 300px;
  }

  img {
    margin: 0 0 2rem 0;
  }

  .h4 {
    margin: 0 0 1rem 0;
  }

  .body1Bold {
    margin: 1rem 0 0 0;
  }

  a {
    font-size: 12px;
    margin: 1rem 0 0 0;
  }
`;

export function getTaskSummaryType(code?: string | null): TaskSummaryType {
  switch (code) {
    case 'SALT401003':
      return 'expired';
    case 'SALT401004':
      return 'activationExceeded';
    case 'SALT401005':
      return 'generationExceeded';
    default:
      return 'denied';
  }
}
